import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Bug, Plus, Eye, RotateCcw, Trash2, Wrench } from "lucide-react";

const PRIO = { alta: "#F87171", media: "#FFD700", bassa: "#8AA" };
const NEXT = { new: "confirmed", confirmed: "in_progress", in_progress: "resolved", resolved: "verified" };
const LABEL = { new: "Nuovo", confirmed: "Confermato", in_progress: "In lavorazione", resolved: "Risolto", verified: "Verificato", reopened: "Riaperto" };

export default function UxIssuesPanel() {
  const [issues, setIssues] = useState([]);
  const [f, setF] = useState({ title: "", description: "", priority: "media", requires_dev: false });

  const load = () => api.get("/ceo/ux-issues").then(({ data }) => setIssues(data.issues || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.title.trim()) return toast.error("Inserisci un titolo");
    try {
      await api.post("/ceo/ux-issues", { ...f, route: window.location.pathname, viewport: `${window.innerWidth}x${window.innerHeight}`, device: navigator.userAgent.slice(0, 80) });
      toast.success("Segnalazione creata"); setF({ title: "", description: "", priority: "media", requires_dev: false }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (i, status) => { try { await api.patch(`/ceo/ux-issues/${i.id}`, { status }); load(); } catch (e) { toast.error("Errore"); } };
  const remove = async (i) => { if (!window.confirm(`Eliminare "${i.title}"?`)) return; try { await api.delete(`/ceo/ux-issues/${i.id}`); load(); } catch (e) { toast.error("Errore"); } };

  return (
    <div className="space-y-5" data-testid="ux-issues-panel">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Bug className="h-5 w-5 text-[#00E676]" /> UX Issues</h2>
        <p className="text-sm text-zinc-500">Segnala problemi UX contestuali (route, viewport, device auto-acquisiti) e gestiscili nel workflow Nuovo → Confermato → In lavorazione → Risolto → Verificato.</p>
      </div>

      <div className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4 space-y-3">
        <input data-testid="ux-title" value={f.title} placeholder="Es. Assistente AI sovrapposto alla X del comune"
          onChange={(e) => setF({ ...f, title: e.target.value })} className="w-full bg-[#0F1016] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" />
        <textarea data-testid="ux-desc" value={f.description} placeholder="Descrizione…" rows={2}
          onChange={(e) => setF({ ...f, description: e.target.value })} className="w-full bg-[#0F1016] border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm" />
        <div className="flex items-center gap-3 flex-wrap">
          <select data-testid="ux-priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="bg-[#0F1016] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
            <option value="alta">Priorità alta</option><option value="media">Priorità media</option><option value="bassa">Priorità bassa</option>
          </select>
          <label className="text-sm text-zinc-400 flex items-center gap-1.5"><input type="checkbox" checked={f.requires_dev} onChange={(e) => setF({ ...f, requires_dev: e.target.checked })} /> 🔧 Richiede sviluppo</label>
          <button data-testid="ux-create" onClick={create} className="ml-auto flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#00E676] text-black font-semibold text-sm"><Plus className="h-4 w-4" /> Crea</button>
        </div>
      </div>

      <div className="space-y-2" data-testid="ux-list">
        {issues.length === 0 && <div className="text-zinc-600 text-sm p-4 rounded-xl bg-[#1C1C1F] border border-white/10">Nessuna segnalazione.</div>}
        {issues.map((i) => (
          <div key={i.id} data-testid={`ux-issue-${i.id}`} className="rounded-2xl bg-[#1C1C1F] border border-white/10 p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2 flex-wrap">{i.title}
                  <span className="text-[10px] uppercase px-1.5 py-0.5 rounded" style={{ color: PRIO[i.priority], border: `1px solid ${PRIO[i.priority]}55` }}>{i.priority}</span>
                  {i.requires_dev && <span className="text-[10px] text-amber-400 flex items-center gap-1"><Wrench className="h-3 w-3" /> sviluppo</span>}
                </div>
                {i.description && <div className="text-sm text-zinc-400 mt-0.5">{i.description}</div>}
                <div className="text-[11px] text-zinc-600 mt-1">{i.route} · {i.viewport} · {i.by}</div>
              </div>
              <span className="text-xs font-semibold text-[#00E676] shrink-0">{LABEL[i.status]}</span>
            </div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {NEXT[i.status] && <button data-testid={`ux-advance-${i.id}`} onClick={() => setStatus(i, NEXT[i.status])} className="text-xs px-3 py-1.5 rounded-full bg-[#00E676] text-black font-semibold">→ {LABEL[NEXT[i.status]]}</button>}
              {(i.status === "verified" || i.status === "resolved") && <button data-testid={`ux-reopen-${i.id}`} onClick={() => setStatus(i, "reopened")} className="text-xs px-3 py-1.5 rounded-full border border-white/15 flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Riapri</button>}
              {i.status === "reopened" && <button onClick={() => setStatus(i, "in_progress")} className="text-xs px-3 py-1.5 rounded-full border border-white/15">→ In lavorazione</button>}
              <button data-testid={`ux-del-${i.id}`} onClick={() => remove(i)} className="text-xs px-2 py-1.5 rounded-full border border-red-500/40 text-red-400 ml-auto"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
